/**
 * Sewing Factory Board — one factory, today, Building-Floors on a wall (multi building/floor
 * filters default to all). See `design-notes.md`.
 *
 * Three queries: `floors` is the panel grid, `lines` fills each panel's chips, `totals` is the
 * headline strip. `floors` and `totals` are separate governed views rather than client roll-ups
 * because floor and factory efficiency are weighted ratios, `SUM(earned)/SUM(worked)` and not the
 * average of a column of percentages (§4.3).
 *
 * The one client-side reshape is grouping `lines` by Building-Floor, which is presentation over a
 * complete result — gated on `linesComplete` and never rendered from a truncated one (§4.3).
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ButtonGroupSelector } from '@/components/report/ButtonGroupSelector';
import { ExportMenu } from '@/components/report/ExportMenu';
import { KioskToggle } from '@/components/report/KioskToggle';
import { useReportFilters } from '@/components/report/useReportFilters';
import { useKioskMode } from '@/hooks/useKioskMode';
import { useReportQuery } from '@/hooks/useReportQuery';
import type { ReportQueryRequest } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { resolveFactory } from '@/lib/factories';
import { cn } from '@/lib/utils';

import { FactoryHeadline, type FactoryTotals } from './cards';
import { useFittedPanels } from './fit';
import { useBoardDay } from '@/reports/sewing-floor-board/useBoardDay';

import {
  METRIC_PARAM,
  METRIC_TABS,
  REFRESH_INTERVAL_MS,
  parseMetric,
  sewingFactoryBoardFilters,
} from './filters';
import { FloorPanels } from './panels';
import type { FactoryRow } from './tokens';
import { useKioskFloorPages } from './useKioskFloorPages';

const REPORT_ID = 'sewing-factory-board';

/**
 * Always-on poll for a board left open on a wall (legacy 120s). Background true so a kiosk or
 * unfocused tab does not freeze — TanStack pauses intervals when the document is hidden otherwise.
 */
const WALL_POLL = {
  staleTime: REFRESH_INTERVAL_MS,
  refetchInterval: REFRESH_INTERVAL_MS,
  refetchIntervalInBackground: true,
} as const;

export default function SewingFactoryBoardReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, status } = useAuth();
  const { isKiosk } = useKioskMode();

  // replace, not push — Back should leave the report rather than walk back through the metric toggle.
  const setUrlParam = useCallback(
    (name: string, value: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set(name, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Keep the URL on the live board day while following the rule (desk and kiosk both auto-refresh).
  const commitBoardDate = useCallback(
    (date: string) => {
      setUrlParam('tran_date', date);
    },
    [setUrlParam],
  );

  const factory = resolveFactory(searchParams.get('factory'), user?.defaultFactory);
  const metric = parseMetric(searchParams.get(METRIC_PARAM));
  const tranDate = useBoardDay(searchParams.get('tran_date'), commitBoardDate);

  const schema = useMemo(() => sewingFactoryBoardFilters(factory), [factory]);
  const { values, setValue } = useReportFilters(schema);

  // Write the factory into the URL, or a shared link resolves to the *recipient's* factory — a
  // different answer under the same address. Compared against the resolved value, not merely
  // absence, so a URL naming a withdrawn factory is corrected rather than left lying.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (searchParams.get('factory') === factory) return;
    setValue('factory', factory);
  }, [status, searchParams, factory, setValue]);

  // Multi-selects: empty string / missing = all (server `_any = 1`). Only pass a list when chosen.
  const building =
    typeof values['building'] === 'string' && values['building'] !== ''
      ? values['building']
      : undefined;
  const floor =
    typeof values['floor'] === 'string' && values['floor'] !== '' ? values['floor'] : undefined;

  const requestFilters = useMemo<ReportQueryRequest['filters']>(
    () => ({
      tran_date: tranDate,
      factory,
      metric,
      ...(building !== undefined ? { building } : {}),
      ...(floor !== undefined ? { floor } : {}),
    }),
    [tranDate, factory, metric, building, floor],
  );

  // No sort: the board is a panel grid, not a sortable table, so it opens and stays in its own
  // grain — building then floor — which the server's default ordering already gives (§4.6).
  const floors = useReportQuery<FactoryRow>(REPORT_ID, requestFilters, {
    view: 'floors',
    ...WALL_POLL,
  });
  const lines = useReportQuery<FactoryRow>(REPORT_ID, requestFilters, {
    view: 'lines',
    ...WALL_POLL,
  });
  const totals = useReportQuery<FactoryTotals>(REPORT_ID, requestFilters, {
    view: 'totals',
    ...WALL_POLL,
  });

  const floorRows = useMemo(() => floors.data?.rows ?? [], [floors.data]);
  const lineRows = useMemo(() => lines.data?.rows ?? [], [lines.data]);

  // The §4.3 gate. Chips are a whole-result reshape, so they render only when the server says it
  // returned everything; a short chip list would read as "this floor has four lines".
  const linesComplete =
    lines.data !== undefined
    && lines.data.truncated !== true
    && lines.data.rows.length === lines.data.total;

  // Kiosk with many floors (e.g. BCA): page 6 at a time so the wall stays readable; desk shows all.
  const {
    visible: visibleFloors,
    page,
    pageCount,
    rotating,
    rangeLabel,
  } = useKioskFloorPages(floorRows, isKiosk);

  // Fit against the cards on screen this page, not the full factory count.
  const { ref: boardRef, tier, columns } = useFittedPanels(isKiosk, visibleFloors.length);

  // The three best floors, by the rank the SERVER assigned — ranking them here would derive the answer (§4.3).
  // Leaders stay factory-wide even while the grid pages, so the strip does not jump each cycle.
  const leaders = useMemo(
    () =>
      floorRows
        .filter((row) => typeof row['floor_rank'] === 'number' && row['floor_rank'] <= 3)
        .sort((a, b) => (a['floor_rank'] as number) - (b['floor_rank'] as number)),
    [floorRows],
  );

  // Export carries the line detail: it is the fullest grain on screen, and the floors roll-up is
  // one aggregation away from it. Columns come from the view's own declaration (§5.9).
  const request: ReportQueryRequest = { view: 'lines', filters: requestFilters };

  const kioskHeading = rotating
    ? `${factory} · floors ${rangeLabel ?? ''} · ${metric}`
    : `${factory} · all floors · ${metric}`;

  return (
    <ReportPage
      title="Sewing Factory Board"
      description="Every sewing floor in one factory, each against its own target for today."
      kiosk={isKiosk}
      // On a wall the factory is what needs naming; which report it is, the reader knows.
      kioskHeading={kioskHeading}
      filters={
        isKiosk ? undefined : (
          <FilterBar
            schema={schema}
            afterFilters={
              <ButtonGroupSelector
                label="Measured by"
                options={METRIC_TABS}
                value={metric}
                onChange={(next) => {
                  setUrlParam(METRIC_PARAM, next); // a declared server filter, so it belongs in the URL and the request
                }}
              />
            }
            actions={
              <>
                <ExportMenu reportId={REPORT_ID} request={request} />
                <KioskToggle />
              </>
            }
          />
        )
      }
      summary={
        <FactoryHeadline
          totals={totals.data?.rows[0]}
          leaders={leaders}
          isLoading={totals.isLoading}
          tier={tier}
        />
      }
      isLoading={floors.isLoading}
      error={floors.error}
      generatedAt={floors.data?.generatedAt}
      dataAsOf={floors.data?.dataAsOf}
      truncated={floors.data?.truncated === true}
      table={
        // The measured box: the height left once the strip and the kiosk bar have taken theirs.
        <div ref={boardRef} className="flex min-h-0 flex-1 flex-col gap-2">
          <FloorPanels
            floors={visibleFloors}
            lines={lineRows}
            linesComplete={linesComplete}
            isLoading={floors.isLoading}
            tier={tier}
            columns={columns}
            isKiosk={isKiosk}
            linkParams={{ factory, tran_date: tranDate, metric }}
          />
          {rotating ? (
            <div
              className="flex flex-none items-center justify-center gap-3 pb-1 text-sm text-muted-foreground"
              aria-live="polite"
            >
              <span className="tabular-nums font-medium text-foreground">
                Floors {rangeLabel}
              </span>
              <span className="flex items-center gap-1.5" aria-hidden>
                {Array.from({ length: pageCount }, (_, index) => (
                  <span
                    key={index}
                    className={cn(
                      'size-2 rounded-full transition-colors',
                      index === page ? 'bg-foreground' : 'bg-muted-foreground/40',
                    )}
                  />
                ))}
              </span>
            </div>
          ) : null}
        </div>
      }
    />
  );
}
